import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { FaPlus, FaTimes, FaBold, FaItalic, FaListUl, FaListOl } from "react-icons/fa";
import { SimpleRichEditor } from './SimpleRichEditor'; // We'll extract this to a separate file

// We use forwardRef to allow the parent component to get a reference to this component
// and call a function to get the form data.
const EditTourForm = forwardRef(({ tour, apiBaseUrl = "https://blackleader-server.create-site.gr" }, ref) => {
  // Set up state with initial values from the tour object  
  const [name, setName] = useState(tour?.name || "");
  const [description, setDescription] = useState(tour?.description || "");
  const [duration, setDuration] = useState(tour?.duration || "");
  const [maxPersons, setMaxPersons] = useState(tour?.maxPersons || 8);
  const [tourImage, setTourImage] = useState(null);
  const [vehicleType, setVehicleType] = useState(tour?.vehicleType || "car");
  const [pricingType, setPricingType] = useState(tour?.pricingType || "perPerson");
  const [pricing, setPricing] = useState(tour?.pricing || {
    car: { lowSeason: "", mediumSeason: "", highSeason: "" },
    minivan: { lowSeason: "", mediumSeason: "", highSeason: "" },
    van: { lowSeason: "", mediumSeason: "", highSeason: "" },
    minibus: { lowSeason: "", mediumSeason: "", highSeason: "" },
  });
  
  // Initialize locations and highlights as arrays of HTML content
  const [locations, setLocations] = useState(
    tour?.locations?.length ? tour.locations : [""]
  );
  const [highlights, setHighlights] = useState(
    tour?.highlights?.length ? tour.highlights : [""]
  );

  // --- Location Handlers ---
  const handleLocationChange = (index, value) => {
    const newLocations = [...locations];
    newLocations[index] = value;
    setLocations(newLocations);
  };

  const addLocation = () => {
    setLocations([...locations, ""]);
  };

  const removeLocation = (index) => {
    if (locations.length > 1) {
      const newLocations = locations.filter((_, i) => i !== index);
      setLocations(newLocations);
    } else {
      setLocations([""]);
    }
  };

  // --- Highlight Handlers ---
  const handleHighlightChange = (index, value) => {
    const newHighlights = [...highlights];
    newHighlights[index] = value;
    setHighlights(newHighlights);
  };

  const addHighlight = () => {
    setHighlights([...highlights, ""]);
  };

  const removeHighlight = (index) => {
    if (highlights.length > 1) {
      const newHighlights = highlights.filter((_, i) => i !== index);
      setHighlights(newHighlights);
    } else {
      setHighlights([""]);
    }
  };

  // --- Pricing Handlers ---
  const handlePricingChange = (vehicle, season, value) => {
    setPricing((prevPricing) => ({
      ...prevPricing,
      [vehicle]: {
        ...prevPricing[vehicle],
        [season]: value === "" ? "" : Number(value),
      },
    }));
  };

  // --- Image Handler ---
  const handleImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setTourImage(event.target.files[0]);
    } else {
      setTourImage(null);
    }
  };

  // Extract plain text from HTML for validation purposes
  const htmlToPlainText = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  // --- Expose function to parent via ref ---
  useImperativeHandle(ref, () => ({
    getFormData: () => {
      // --- Basic Validation Example ---
      if (!name || !description || !duration || !maxPersons) {
        console.error("Missing basic fields");
        return null; // Indicate validation failure
      }

      // Check if description has content
      if (!htmlToPlainText(description).trim()) {
        console.error("Description cannot be empty");
        return null;
      }

      // Check if all pricing fields are filled (or handle optional pricing)
      for (const vehicle in pricing) {
        for (const season in pricing[vehicle]) {
          if (pricing[vehicle][season] === "" || pricing[vehicle][season] < 0) {
            console.error(
              `Missing or invalid price for ${vehicle} - ${season}`
            );
            return null; // Indicate validation failure
          }
        }
      }

      // Filter out empty locations and highlights
      const finalLocations = locations.filter(
        (loc) => htmlToPlainText(loc).trim() !== ""
      );
      const finalHighlights = highlights.filter(
        (h) => htmlToPlainText(h).trim() !== ""
      );

      if (finalLocations.length === 0) {
        console.error("At least one location is required");
        return null;
      }

      if (finalHighlights.length === 0) {
        console.error("At least one highlight is required");
        return null;
      }      // Return collected data
      return {
        name,
        description,
        duration: Number(duration),
        maxPersons: Number(maxPersons),
        tourImage, // The file object itself
        vehicleType,
        pricingType, // Added pricing type option
        pricing, // The nested pricing object
        locations: finalLocations, // Filtered array
        highlights: finalHighlights, // Filtered array
        isActive: tour?.isActive ?? true,
      };
    },
  }));

  return (
    <div id="editTourFormReact" className="space-y-6 text-left">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>

      {/* Description with Simple Rich Editor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <SimpleRichEditor
          initialValue={description}
          onChange={setDescription}
          placeholder="Enter tour description..."
        />
      </div>

      {/* Duration & Max Persons */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Duration (hours)
          </label>
          <input
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            type="number"
            min="1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Max Persons
          </label>
          <input
            value={maxPersons}
            onChange={(e) => setMaxPersons(e.target.value)}
            type="number"
            min="1"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
      </div>

      {/* Tour Image */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Tour Image
        </label>
        <input
          id="tourImage"
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
        />
        <p className="text-xs text-gray-500 mt-1">
          Upload a new image for the tour (Max Size: 5MB)
        </p>
        
        {/* Display current tour image if it exists */}
        {tour?.imageUrl && (
          <div className="mt-2">
            <p className="text-xs text-gray-500">Current image:</p>
            <div className="h-44 w-full mt-1 rounded bg-gray-100 p-1">
              <img 
                src={`${apiBaseUrl}${tour.imageUrl.startsWith("/") ? "" : "/"}${tour.imageUrl}`} 
                className="h-full w-full object-cover rounded" 
                alt="Current tour image" 
              />
            </div>
          </div>
        )}
      </div>

      {/* Vehicle Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Default Vehicle Type
        </label>
        <select
          value={vehicleType}
          onChange={(e) => setVehicleType(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="car">Car</option>
          <option value="minivan">Mini Van</option>
          <option value="van">Van</option>
          <option value="minibus">Mini Bus</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Select the default type of vehicle for the tour.
        </p>      </div>
      
      {/* Pricing Type */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Pricing Type
        </label>
        <div className="flex space-x-4">
          <div className="flex items-center">
            <input
              id="pricing-per-person"
              name="pricingType"
              type="radio"
              value="perPerson"
              checked={pricingType === "perPerson"}
              onChange={() => setPricingType("perPerson")}
              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="pricing-per-person" className="ml-2 block text-sm text-gray-700">
              Per Person
            </label>
          </div>
          <div className="flex items-center">
            <input
              id="pricing-fixed"
              name="pricingType"
              type="radio"
              value="fixed"
              checked={pricingType === "fixed"}
              onChange={() => setPricingType("fixed")}
              className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="pricing-fixed" className="ml-2 block text-sm text-gray-700">
              Fixed Price
            </label>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {pricingType === "perPerson" ? 
            "Per Person pricing: The price will be multiplied by the number of participants." : 
            "Fixed pricing: The same price regardless of the number of participants."}
        </p>
      </div>

      {/* --- Pricing Section --- */}
      <div className="mt-4 border-t pt-4">
        <h3 className="text-md font-bold text-gray-800 mb-3">
          Pricing by Vehicle Type (€)
        </h3>
        {Object.entries(pricing).map(([vehicleKey, seasons]) => (
          <div
            key={vehicleKey}
            className={`p-3 mb-4 ${
              vehicleKey === "car"
                ? "bg-blue-50 border-blue-100"
                : vehicleKey === "minivan"
                ? "bg-green-50 border-green-100"
                : vehicleKey === "van"
                ? "bg-purple-50 border-purple-100"
                : "bg-amber-50 border-amber-100" // minibus
            } rounded-lg border`}
          >
            <h4
              className={`text-sm font-semibold mb-2 ${
                vehicleKey === "car"
                  ? "text-blue-700"
                  : vehicleKey === "minivan"
                  ? "text-green-700"
                  : vehicleKey === "van"
                  ? "text-purple-700"
                  : "text-amber-700" // minibus
              }`}
            >
              {vehicleKey.charAt(0).toUpperCase() + vehicleKey.slice(1)} Pricing
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(seasons).map(([seasonKey, value]) => (
                <div key={seasonKey}>
                  <label className="block text-xs font-medium text-gray-700">
                    {seasonKey
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (str) => str.toUpperCase())}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={value}
                    onChange={(e) =>
                      handlePricingChange(vehicleKey, seasonKey, e.target.value)
                    }
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-${
                      vehicleKey === "car"
                        ? "blue"
                        : vehicleKey === "minivan"
                        ? "green"
                        : vehicleKey === "van"
                        ? "purple"
                        : "amber" // minibus
                    }-500 focus:ring-${
                      vehicleKey === "car"
                        ? "blue"
                        : vehicleKey === "minivan"
                        ? "green"
                        : vehicleKey === "van"
                        ? "purple"
                        : "amber" // minibus
                    }-500 text-sm`}
                    required
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* --- Locations with Simple Rich Editor --- */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Locations
        </label>
        {locations.map((location, index) => (
          <div key={index} className="flex items-start space-x-2 mb-4">
            <div className="flex-grow min-w-0">
              <SimpleRichEditor
                initialValue={location}
                onChange={(content) => handleLocationChange(index, content)}
                placeholder={`Location ${index + 1}`}
              />
            </div>
            <button
              type="button"
              onClick={() => removeLocation(index)}
              className={`p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100 ${
                locations.length <= 1 ? "opacity-50 cursor-not-allowed" : ""
              } mt-2`}
              disabled={locations.length <= 1}
              aria-label="Remove Location"
            >
              <FaTimes size="1em" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addLocation}
          className="mt-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center"
        >
          <FaPlus className="mr-1" size="0.8em" /> Add Location
        </button>
      </div>

      {/* --- Highlights with Simple Rich Editor --- */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Highlights
        </label>
        {highlights.map((highlight, index) => (
          <div key={index} className="flex items-start space-x-2 mb-4">
            <div className="flex-grow min-w-0">
              <SimpleRichEditor
                initialValue={highlight}
                onChange={(content) => handleHighlightChange(index, content)}
                placeholder={`Highlight ${index + 1}`}
              />
            </div>
            <button
              type="button"
              onClick={() => removeHighlight(index)}
              className={`p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-100 ${
                highlights.length <= 1 ? "opacity-50 cursor-not-allowed" : ""
              } mt-2`}
              disabled={highlights.length <= 1}
              aria-label="Remove Highlight"
            >
              <FaTimes size="1em" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addHighlight}
          className="mt-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center"
        >
          <FaPlus className="mr-1" size="0.8em" /> Add Highlight
        </button>
      </div>
    </div>
  );
});

export default EditTourForm;
